import React, { forwardRef } from 'react';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardActions from '@material-ui/core/CardActions';
import { useTheme } from '@material-ui/core/styles';
import MuiAlert from '@material-ui/lab/Alert';
import { THEME } from 'GlobalStyles';
import { useSnackbar } from 'notistack';
import ConditionalActionWrapper from 'UI/components/atoms/ConditionalWrapper';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import { icon as iconDimensions } from 'UI/constants/dimensions';
import { notificationIcons } from 'UI/constants/notifications';
import {
  AnnouncementIcon,
  CloseIcon,
  colors,
  OutlinedWarningIcon,
  SuccessIcon,
  SvgDelete
} from 'UI/res';
import { ThemeModes } from 'UI/theme/themeMode';

import { useAlertStyles, useNotificationStyles, useStyles } from './styles';

const { customColors } = THEME.palette;
const { white, black } = customColors;

const sharedProps = { fill: white };
const errorIconSize = 20;

const severityValues = {
  warning: {
    icon: <OutlinedWarningIcon fill={white} />,
    props: sharedProps
  },
  error: {
    icon: <SvgDelete />,
    props: { ...sharedProps, width: errorIconSize, height: errorIconSize }
  },
  info: {
    icon: <AnnouncementIcon fill={white} />,
    props: sharedProps
  },
  success: {
    icon: <SuccessIcon fill={white} />,
    props: sharedProps
  }
};

/**
 * Custom snackbar notification component with alert and notification modes
 * @param {Object} props
 * @param {string} [props.severity='info'] - Alert severity: 'warning', 'error', 'info', or 'success'
 * @param {string} props.title - Notification title text
 * @param {string} [props.body=''] - Notification body text
 * @param {boolean} [props.isNotification] - When true, renders in notification mode with different styling
 * @param {string} [props.color] - CSS color string for the notification indicator bar
 * @param {string} [props.icon] - Key to look up in notificationIcons array
 * @param {Function} [props.onClick] - Click handler for notification mode
 * @param {string|number} [props.id] - Snackbar ID injected by notistack for dismissal
 * @param {React.Ref} ref - Forwarded ref attached to the root Card element
 */
const CustomSnackbar = forwardRef((props, ref) => {
  const {
    severity = 'info',
    title,
    body = '',
    isNotification,
    color = colors.darkGrey,
    icon,
    onClick = () => {}
  } = props;

  const classes = useStyles({
    color
  });
  const theme = useTheme();
  const { closeSnackbar } = useSnackbar();
  // Dark mode uses a tonal alert: severity-colored icon and secondary-text close button.
  const isDarkAlert = theme.palette.type === ThemeModes.Dark && !isNotification;
  const alertIconFill = isDarkAlert ? theme.palette[severity]?.main : white;
  const alertCloseFill = isDarkAlert ? theme.palette.text.secondary : white;
  const alertClasses = useAlertStyles();
  const handleDismiss = () => closeSnackbar(props.id);
  const notificationClasses = useNotificationStyles();

  const notification = notificationIcons.find(n => {
    return n.key === icon;
  }) || { icon: <AnnouncementIcon /> };

  const Icon = () =>
    isNotification
      ? {
          ...notification?.icon,
          props: { fill: black, size: iconDimensions.size }
        }
      : {
          ...severityValues[severity].icon,
          props: { ...severityValues[severity].props, fill: alertIconFill }
        };

  return (
    <Card className={classes.root} ref={ref}>
      <ConditionalActionWrapper
        condition={isNotification}
        wrapper={children => <CardActionArea onClick={onClick}>{children}</CardActionArea>}
      >
        {isNotification && <div className={classes.indicator} />}
        <MuiAlert
          icon={<Icon />}
          elevation={6}
          variant="filled"
          classes={isNotification ? notificationClasses : alertClasses}
          severity={!isNotification ? severity : undefined}
        >
          <div className={classes.textContainer}>
            <div className={classes.title}>{title}</div>
            <div className={isDarkAlert ? `${classes.body} ${classes.mutedBody}` : classes.body}>
              {body}
            </div>
          </div>
        </MuiAlert>
      </ConditionalActionWrapper>
      <CardActions className={classes.buttonContainer}>
        <FPIconButton className={classes.closeButton} size="md" onClick={handleDismiss}>
          <CloseIcon fill={isNotification ? black : alertCloseFill} />
        </FPIconButton>
      </CardActions>
    </Card>
  );
});

export default CustomSnackbar;
